import type { AssignedAssetSetTaxonomy } from '../components/AssignedAssetSetClassificationTree.tsx';
import { compareNamesInsensitive } from './nameComparison.ts';

const ASSET_SET_TYPE_ALL = 2;

export type FacilityAssetSetTreeRowId = string;

export interface FacilityAssetSetTreeNode {
    id: FacilityAssetSetTreeRowId;
    name: string;
    itemType: 'knowledgeBook' | 'schema' | 'tag' | 'root';
    source?: 'facility' | 'knowledgeBook';
    tagId?: string;
    assetSetId?: string;
    assetSet?: IAssetSet | null;
    parentId: FacilityAssetSetTreeRowId | null;
    type?: AssetSetType;
    versionToken?: string;
    facilityId?: string;
    knowledgeBookId?: string | null;
    isSystem?: boolean;
}

export interface FacilityAssetSetTreeIndex {
    rootIds: FacilityAssetSetTreeRowId[];
    byId: Map<FacilityAssetSetTreeRowId, FacilityAssetSetTreeNode>;
    childrenById: Map<FacilityAssetSetTreeRowId, FacilityAssetSetTreeRowId[]>;
    parentById: Map<FacilityAssetSetTreeRowId, FacilityAssetSetTreeRowId | null>;
    occurrenceIdsByAssetSetId: Map<string, FacilityAssetSetTreeRowId[]>;
    lowerNameById: Map<FacilityAssetSetTreeRowId, string>;
    descendantAssetSetIdsByRowId: Map<FacilityAssetSetTreeRowId, string[]>;
}

export interface FacilityAssetSetTreeProjection {
    includedRowIds: Set<FacilityAssetSetTreeRowId> | null;
    forcedOpenRowIds: Set<FacilityAssetSetTreeRowId>;
}

export interface BuildFacilityAssetSetTreeOptions {
    includeKnowledgeBookRoot?: boolean;
    selectedKnowledgeBook?: IKnowledgeBook | null;
    knowledgeBookAssetSets?: readonly IAssetSet[];
    /** Fixed root label, so the branch does not read as the Knowledge Book itself. Defaults to its name. */
    knowledgeBookRootLabel?: string;
}

export interface ProjectFacilityAssetSetTreeOptions {
    searchQuery?: string;
    selectedOntologyOnly?: boolean;
    selectedOntologyId?: string | null;
    isKnowledgeBookAssetSetApplicable?: (assetSet: IAssetSet) => boolean;
    removeEmptyBranches?: boolean;
}

const ensureChildren = (
    childrenById: Map<FacilityAssetSetTreeRowId, FacilityAssetSetTreeRowId[]>,
    id: FacilityAssetSetTreeRowId
) => {
    let children = childrenById.get(id);
    if (!children) {
        children = [];
        childrenById.set(id, children);
    }
    return children;
};

const addNode = (index: FacilityAssetSetTreeIndex, node: FacilityAssetSetTreeNode) => {
    index.byId.set(node.id, node);
    index.parentById.set(node.id, node.parentId);
    index.lowerNameById.set(node.id, node.name.toLocaleLowerCase());
    ensureChildren(index.childrenById, node.id);
};

const addOccurrence = (
    index: FacilityAssetSetTreeIndex,
    assetSet: IAssetSet,
    parentId: FacilityAssetSetTreeRowId,
    rowId: FacilityAssetSetTreeRowId,
    source: 'facility' | 'knowledgeBook'
) => {
    addNode(index, {
        id: rowId,
        name: assetSet.Name,
        itemType: 'root',
        source,
        assetSetId: assetSet.ID,
        assetSet,
        parentId,
        type: assetSet.Type,
        versionToken: assetSet.VersionToken,
        facilityId: assetSet.FacilityID,
        knowledgeBookId: assetSet.KnowledgeBookID,
        isSystem: assetSet.IsSystem,
    });
    ensureChildren(index.childrenById, parentId).push(rowId);

    const occurrences = index.occurrenceIdsByAssetSetId.get(assetSet.ID) ?? [];
    occurrences.push(rowId);
    index.occurrenceIdsByAssetSetId.set(assetSet.ID, occurrences);
};

const createsTagCycle = (tag: IClassificationTag, tagsById: ReadonlyMap<string, IClassificationTag>) => {
    const visited = new Set<string>([tag.ID]);
    let parentId = tag.ParentID;
    while (parentId) {
        if (visited.has(parentId)) return true;
        visited.add(parentId);
        parentId = tagsById.get(parentId)?.ParentID;
    }
    return false;
};

export const buildAssetSetTreeIndex = (
    assignedTaxonomies: readonly AssignedAssetSetTaxonomy[],
    assetSets: readonly IAssetSet[],
    options: BuildFacilityAssetSetTreeOptions = {}
): FacilityAssetSetTreeIndex => {
    const index: FacilityAssetSetTreeIndex = {
        rootIds: [],
        byId: new Map(),
        childrenById: new Map(),
        parentById: new Map(),
        occurrenceIdsByAssetSetId: new Map(),
        lowerNameById: new Map(),
        descendantAssetSetIdsByRowId: new Map(),
    };

    const selectedKnowledgeBook = options.selectedKnowledgeBook;
    if (options.includeKnowledgeBookRoot !== false && selectedKnowledgeBook?.ID) {
        const knowledgeBookRowId = `kb:${selectedKnowledgeBook.ID}`;
        addNode(index, {
            id: knowledgeBookRowId,
            name: options.knowledgeBookRootLabel ?? selectedKnowledgeBook.Name,
            itemType: 'knowledgeBook',
            source: 'knowledgeBook',
            knowledgeBookId: selectedKnowledgeBook.ID,
            parentId: null,
        });
        index.rootIds.push(knowledgeBookRowId);

        (options.knowledgeBookAssetSets ?? [])
            .filter(assetSet => assetSet.Type !== ASSET_SET_TYPE_ALL)
            .sort((left, right) => compareNamesInsensitive(left.Name, right.Name))
            .forEach(assetSet => addOccurrence(
                index,
                assetSet,
                knowledgeBookRowId,
                `${knowledgeBookRowId}:${assetSet.ID}`,
                'knowledgeBook'
            ));
    }

    const tagRowIds = new Map<string, FacilityAssetSetTreeRowId>();
    assignedTaxonomies.forEach(({ schema, taxonomy }) => {
        const schemaId = `schema:${schema.ID}`;
        addNode(index, {
            id: schemaId,
            name: schema.Name,
            itemType: 'schema',
            source: 'facility',
            parentId: null,
        });
        index.rootIds.push(schemaId);

        const tags = taxonomy.ClassificationTags ?? [];
        const tagsById = new Map(tags.map(tag => [tag.ID, tag] as const));
        tags.forEach(tag => {
            const tagId = `tag:${tag.ID}`;
            tagRowIds.set(tag.ID, tagId);
            addNode(index, {
                id: tagId,
                name: tag.Name,
                itemType: 'tag',
                source: 'facility',
                tagId: tag.ID,
                parentId: null,
            });
        });
        tags.forEach(tag => {
            const tagId = tagRowIds.get(tag.ID)!;
            const parentTagId = tag.ParentID ? tagRowIds.get(tag.ParentID) : undefined;
            const parentId = parentTagId && !createsTagCycle(tag, tagsById) ? parentTagId : schemaId;
            const node = index.byId.get(tagId)!;
            node.parentId = parentId;
            index.parentById.set(tagId, parentId);
            ensureChildren(index.childrenById, parentId).push(tagId);
        });
    });

    assetSets.forEach(assetSet => {
        const seenTagIds = new Set<string>();
        (assetSet.ClassificationTags ?? []).forEach(tagId => {
            if (seenTagIds.has(tagId)) return;
            seenTagIds.add(tagId);

            const parentId = tagRowIds.get(tagId);
            if (!parentId) return;
            addOccurrence(index, assetSet, parentId, `${parentId}:${assetSet.ID}`, 'facility');
        });
    });

    index.childrenById.forEach(children => children.sort((leftId, rightId) => {
        const left = index.byId.get(leftId)!;
        const right = index.byId.get(rightId)!;
        if (left.itemType !== right.itemType) {
            return left.itemType === 'tag' ? -1 : 1;
        }
        return compareNamesInsensitive(left.name, right.name);
    }));

    const resolving = new Set<FacilityAssetSetTreeRowId>();
    const resolveDescendants = (rowId: FacilityAssetSetTreeRowId): string[] => {
        const cached = index.descendantAssetSetIdsByRowId.get(rowId);
        if (cached) return cached;
        if (resolving.has(rowId)) return [];
        resolving.add(rowId);
        const node = index.byId.get(rowId);
        const ids = new Map<string, string>();
        if (node?.assetSetId) ids.set(node.assetSetId.toLocaleLowerCase(), node.assetSetId);
        (index.childrenById.get(rowId) ?? []).forEach(childId => {
            resolveDescendants(childId).forEach(assetSetId =>
                ids.set(assetSetId.toLocaleLowerCase(), assetSetId));
        });
        resolving.delete(rowId);
        const result = [...ids.values()];
        index.descendantAssetSetIdsByRowId.set(rowId, result);
        return result;
    };
    index.byId.forEach((_node, rowId) => resolveDescendants(rowId));

    return index;
};

const includeAncestors = (
    index: FacilityAssetSetTreeIndex,
    rowId: FacilityAssetSetTreeRowId,
    included: Set<FacilityAssetSetTreeRowId>,
    forcedOpen?: Set<FacilityAssetSetTreeRowId>
) => {
    const visited = new Set<FacilityAssetSetTreeRowId>();
    let parentId = index.parentById.get(rowId) ?? null;
    while (parentId && !visited.has(parentId)) {
        visited.add(parentId);
        included.add(parentId);
        forcedOpen?.add(parentId);
        parentId = index.parentById.get(parentId) ?? null;
    }
};

const includeDescendants = (
    index: FacilityAssetSetTreeIndex,
    rowId: FacilityAssetSetTreeRowId,
    included: Set<FacilityAssetSetTreeRowId>,
    allowed: ReadonlySet<FacilityAssetSetTreeRowId> | null,
    forcedOpen: Set<FacilityAssetSetTreeRowId>
) => {
    const pending = [...(index.childrenById.get(rowId) ?? [])];
    if (pending.length > 0) forcedOpen.add(rowId);
    const visited = new Set<FacilityAssetSetTreeRowId>();
    while (pending.length) {
        const childId = pending.pop()!;
        if (visited.has(childId)) continue;
        visited.add(childId);
        if (!allowed || allowed.has(childId)) {
            included.add(childId);
            const children = index.childrenById.get(childId) ?? [];
            if (children.length > 0) forcedOpen.add(childId);
            pending.push(...children);
        }
    }
};

export const projectTree = (
    index: FacilityAssetSetTreeIndex,
    options: ProjectFacilityAssetSetTreeOptions
): FacilityAssetSetTreeProjection => {
    const selectedOntologyOnly = !!options.selectedOntologyOnly;
    const query = (options.searchQuery ?? '').trim().toLocaleLowerCase();
    const nonEmptyIncluded = options.removeEmptyBranches
        ? new Set([...index.byId.keys()].filter(rowId =>
            (index.descendantAssetSetIdsByRowId.get(rowId)?.length ?? 0) > 0))
        : null;
    if (!selectedOntologyOnly && !query && !nonEmptyIncluded) {
        return { includedRowIds: null, forcedOpenRowIds: new Set() };
    }

    let ontologyIncluded: Set<FacilityAssetSetTreeRowId> | null = null;
    if (selectedOntologyOnly) {
        ontologyIncluded = new Set();
        const normalizedOntologyId = options.selectedOntologyId?.toLocaleLowerCase();
        if (normalizedOntologyId) {
            index.byId.forEach(node => {
                if (node.itemType !== 'root' || !node.assetSet) return;
                const applicable = node.source === 'knowledgeBook'
                    ? !!options.isKnowledgeBookAssetSetApplicable?.(node.assetSet)
                    : (node.assetSet.ApplicableOntologyIDs ?? [])
                        .some(id => id.toLocaleLowerCase() === normalizedOntologyId);
                if (!applicable) return;

                ontologyIncluded!.add(node.id);
                includeAncestors(index, node.id, ontologyIncluded!);
            });
        }
    }

    const allowedRows = ontologyIncluded && nonEmptyIncluded
        ? new Set([...ontologyIncluded].filter(rowId => nonEmptyIncluded.has(rowId)))
        : ontologyIncluded ?? nonEmptyIncluded;

    if (!query) {
        return { includedRowIds: allowedRows!, forcedOpenRowIds: new Set() };
    }

    const included = new Set<FacilityAssetSetTreeRowId>();
    const forcedOpen = new Set<FacilityAssetSetTreeRowId>();
    index.byId.forEach((_node, rowId) => {
        if (allowedRows && !allowedRows.has(rowId)) return;
        if (!index.lowerNameById.get(rowId)?.includes(query)) return;

        included.add(rowId);
        includeAncestors(index, rowId, included, forcedOpen);
        includeDescendants(index, rowId, included, allowedRows, forcedOpen);
    });

    return { includedRowIds: included, forcedOpenRowIds: forcedOpen };
};

export const getFirstVisibleAssetSetOccurrence = (
    index: FacilityAssetSetTreeIndex,
    projection: FacilityAssetSetTreeProjection,
    assetSetId: string
): FacilityAssetSetTreeRowId | null => {
    const occurrences = index.occurrenceIdsByAssetSetId.get(assetSetId) ?? [];
    return occurrences.find(rowId => !projection.includedRowIds || projection.includedRowIds.has(rowId)) ?? null;
};

export const isAssetSetOccurrenceExpandedIntoView = (
    index: FacilityAssetSetTreeIndex,
    projection: FacilityAssetSetTreeProjection,
    expandedRowIds: ReadonlySet<FacilityAssetSetTreeRowId>,
    assetSetId: string
) => (index.occurrenceIdsByAssetSetId.get(assetSetId) ?? []).some(rowId => {
    if (projection.includedRowIds && !projection.includedRowIds.has(rowId)) return false;

    const visited = new Set<FacilityAssetSetTreeRowId>();
    let parentId = index.parentById.get(rowId) ?? null;
    while (parentId && !visited.has(parentId)) {
        visited.add(parentId);
        if (!expandedRowIds.has(parentId) && !projection.forcedOpenRowIds.has(parentId)) return false;
        parentId = index.parentById.get(parentId) ?? null;
    }
    return true;
});
